import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import {
  Bold, Italic, List, ListOrdered, Quote, Heading1, Heading2, Heading3,
  Link as LinkIcon, Image as ImageIcon, Undo, Redo, Pilcrow, Minus,
} from "lucide-react";

function ToolButton({ onClick, active, disabled, children, testid, title }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className="p-1.5 transition-colors"
      style={{
        border: "1px solid var(--upr-border)",
        background: active ? "var(--upr-fg)" : "transparent",
        color: active ? "var(--upr-bg)" : "var(--upr-fg)",
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
}

export default function RichTextEditor({ value, onChange, testid = "rich-editor", placeholder = "Write something…", simple = false }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: simple ? [1, 2] : [2, 3] }, link: false }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" } }),
      Image,
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "upr-editor upr-prose",
        "data-testid": `${testid}-area`,
        "data-placeholder": placeholder,
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Tiptap returns "<p></p>" for an empty doc; normalise to "".
      onChange(html === "<p></p>" ? "" : html);
    },
  });

  // Sync external value changes (e.g. when opening a different post).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || "";
    if (next !== current) {
      editor.commands.setContent(next, false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL", prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const addImage = () => {
    const url = window.prompt("Image URL");
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  };

  return (
    <div data-testid={testid} style={{ border: "1px solid var(--upr-border)" }}>
      <div className="flex flex-wrap gap-1 p-1.5" style={{ borderBottom: "1px solid var(--upr-border)", background: "var(--upr-surface)" }}>
        <ToolButton testid={`${testid}-bold`} title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={13} /></ToolButton>
        <ToolButton testid={`${testid}-italic`} title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={13} /></ToolButton>
        {simple ? (
          <>
            <ToolButton testid={`${testid}-h1`} title="Heading 1" active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}><Heading1 size={13} /></ToolButton>
            <ToolButton testid={`${testid}-h2`} title="Heading 2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={13} /></ToolButton>
            <ToolButton testid={`${testid}-paragraph`} title="Paragraph" active={editor.isActive("paragraph")} onClick={() => editor.chain().focus().setParagraph().run()}><Pilcrow size={13} /></ToolButton>
          </>
        ) : (
          <>
            <ToolButton testid={`${testid}-h2`} title="Heading 2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={13} /></ToolButton>
            <ToolButton testid={`${testid}-h3`} title="Heading 3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 size={13} /></ToolButton>
            <ToolButton testid={`${testid}-paragraph`} title="Paragraph" active={editor.isActive("paragraph")} onClick={() => editor.chain().focus().setParagraph().run()}><Pilcrow size={13} /></ToolButton>
            <ToolButton testid={`${testid}-ul`} title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={13} /></ToolButton>
            <ToolButton testid={`${testid}-ol`} title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={13} /></ToolButton>
            <ToolButton testid={`${testid}-quote`} title="Blockquote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={13} /></ToolButton>
            <ToolButton testid={`${testid}-hr`} title="Horizontal rule" onClick={() => editor.chain().focus().setHorizontalRule().run()}><Minus size={13} /></ToolButton>
            <ToolButton testid={`${testid}-link`} title="Link" active={editor.isActive("link")} onClick={setLink}><LinkIcon size={13} /></ToolButton>
            <ToolButton testid={`${testid}-image`} title="Image" onClick={addImage}><ImageIcon size={13} /></ToolButton>
          </>
        )}
        <div className="ml-auto flex gap-1">
          <ToolButton testid={`${testid}-undo`} title="Undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}><Undo size={13} /></ToolButton>
          <ToolButton testid={`${testid}-redo`} title="Redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}><Redo size={13} /></ToolButton>
        </div>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
